import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../apiClient";

export interface EscalationPolicy {
  id: number;
  budy_organization_id: number;
  enabled: boolean;
  days_overdue_min: number | null;
  no_reply_days_min: number | null;
  min_amount: number | null;
  trigger_on_llm_escalate: boolean;
  trigger_on_intention_status: string[] | null;
  entry_mode: "ANY" | "ALL";
}

export interface EscalationLevel {
  id: number;
  budy_organization_id: number;
  sequence_order: number;
  name: string;
  days_after_previous_level: number;
  recipient_type: "INTERNAL" | "ESCALATION_CONTACT";
  internal_recipient_mode: "SPECIFIC_USER" | "ASSIGNED_AGENTS" | null;
  internal_recipient_emails: string[] | null;
  recipient_user_id: number | null;
  target_position_codes: number[] | null;
  include_manual_escalation_contacts: boolean;
  email_template_id: number | null;
  auto_send: boolean;
  enabled: boolean;
}

export type EscalationLevelCreate = {
  sequence_order?: number;
  name: string;
  days_after_previous_level?: number;
  recipient_type: EscalationLevel["recipient_type"];
  internal_recipient_mode?: EscalationLevel["internal_recipient_mode"];
  internal_recipient_emails?: string[] | null;
  recipient_user_id?: number | null;
  target_position_codes?: number[] | null;
  include_manual_escalation_contacts?: boolean;
  email_template_id?: number | null;
  auto_send?: boolean;
  enabled?: boolean;
};

export interface PaymentEscalationState {
  payment_external_id: string;
  escalated_at: string;
  current_level_sequence: number;
  last_level_executed_at: string | null;
  entry_reason: string;
  deescalated_at: string | null;
  total_levels: number;
  next_level_name: string | null;
  days_until_next_level: number | null;
  pending_approval: boolean;
  events: Array<{
    id: number;
    status: string;
    level_name: string | null;
    draft_uid: string | null;
    recipients: string[] | null;
    executed_at: string | null;
  }>;
}

export const escalationKeys = {
  policy: ["escalation-policy"] as const,
  levels: ["escalation-levels"] as const,
  payment: (id: string) => ["payment-escalation", id] as const,
};

export function useEscalationPolicyQuery() {
  return useQuery({
    queryKey: escalationKeys.policy,
    queryFn: async () => {
      const { data } = await apiClient.get<EscalationPolicy>(
        "/organization/escalation-policy",
      );
      return data;
    },
  });
}

export function useUpdateEscalationPolicyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<EscalationPolicy>) => {
      const { data } = await apiClient.put<EscalationPolicy>(
        "/organization/escalation-policy",
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: escalationKeys.policy }),
  });
}

export function useEscalationLevelsQuery() {
  return useQuery({
    queryKey: escalationKeys.levels,
    queryFn: async () => {
      const { data } = await apiClient.get<EscalationLevel[]>(
        "/organization/escalation-levels",
      );
      return data;
    },
  });
}

export function useCreateEscalationLevelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EscalationLevelCreate) => {
      const { data } = await apiClient.post<EscalationLevel>(
        "/organization/escalation-levels",
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: escalationKeys.levels }),
  });
}

export function useUpdateEscalationLevelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      levelId,
      ...payload
    }: { levelId: number } & Partial<EscalationLevel>) => {
      const { data } = await apiClient.patch<EscalationLevel>(
        `/organization/escalation-levels/${levelId}`,
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: escalationKeys.levels }),
  });
}

export function useDeleteEscalationLevelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (levelId: number) => {
      await apiClient.delete(`/organization/escalation-levels/${levelId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: escalationKeys.levels }),
  });
}

export function usePaymentEscalationQuery(paymentExternalId?: string) {
  return useQuery({
    queryKey: escalationKeys.payment(paymentExternalId ?? ""),
    enabled: Boolean(paymentExternalId),
    queryFn: async () => {
      const { data } = await apiClient.get<PaymentEscalationState | null>(
        `/payments/${paymentExternalId}/escalation`,
      );
      return data;
    },
  });
}

export function useManualEscalationMutation(paymentExternalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (action: "escalate" | "deescalate") => {
      const { data } = await apiClient.post(
        `/payments/${paymentExternalId}/escalation`,
        { action },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: escalationKeys.payment(paymentExternalId) });
    },
  });
}

export function useApproveEscalationDraftMutation() {
  return useMutation({
    mutationFn: async (draftUid: string) => {
      const { data } = await apiClient.post(`/email/drafts/${draftUid}/approve`);
      return data;
    },
  });
}

export function useRejectEscalationDraftMutation() {
  return useMutation({
    mutationFn: async (draftUid: string) => {
      const { data } = await apiClient.post(`/email/drafts/${draftUid}/reject`);
      return data;
    },
  });
}
